'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleRows } from '@/components/chat/BubbleBlock';
import { saveFromChat } from '@/lib/chat/block-actions';
import {
  partsChecklistItems,
  partsEmptyLabel,
  partsFootnote,
  partsLeftLabel,
  partsQuantityLabel,
  type PartsChecklistBlock,
  type PartsPickedUp,
} from '@/lib/chat/blocks/parts-checklist';

/**
 * What to pick up this week, as boxes to tick (`parts-checklist.ts`).
 *
 * A tick SAVES on the tap (`saveFromChat` → `/api/jobs/[id]/part-pickups`), so
 * the lad at the merchant and the office are looking at the same list. It
 * reaches nobody, which is why it is a direct write and not an approval.
 *
 * The box moves at once; if the server refuses, it goes back and the server's
 * own sentence sits under that line (#35, #42 — inline, never a toast). The
 * "left to pick up" count is the only number here and it is a count of the
 * boxes on screen. There are no prices on this block to show (#23).
 *
 * On an older reply (`live` false) the boxes are as they stood then and do
 * not tap: somebody may have ticked since, and a stale tick toggled from a
 * scrolled-back reply would untick their work.
 */
export function PartsChecklist({ block, live, onOpen }: {
  block: PartsChecklistBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const key = (jobId: string, lineId: string) => `${jobId}:${lineId}`;
  const [picked, setPicked] = useState<Record<string, PartsPickedUp | null>>(() => {
    const init: Record<string, PartsPickedUp | null> = {};
    for (const job of block.jobs) for (const item of job.items) init[key(job.jobId, item.lineId)] = item.picked;
    return init;
  });
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const total = partsChecklistItems(block).length;
  const left = Object.values(picked).filter(p => !p).length;
  const empty = partsEmptyLabel(block);
  const footnote = partsFootnote(block);

  const toggle = async (jobId: string, lineId: string, source: 'quote' | 'part' = 'quote') => {
    const k = key(jobId, lineId);
    if (!live || busy[k]) return;
    const was = picked[k] ?? null;
    setPicked(prev => ({ ...prev, [k]: was ? null : { by: 'You', at: new Date().toISOString() } }));
    setBusy(prev => ({ ...prev, [k]: true }));
    setErrors(prev => ({ ...prev, [k]: null }));
    // A part written down on the job ticks its own row (collected / back to
    // in); a quote line is added to the job's parts as collected. One table.
    const body = source === 'part' ? { partId: lineId } : { quoteLineItemId: lineId };
    const out = await saveFromChat(was ? 'DELETE' : 'POST', `/api/jobs/${encodeURIComponent(jobId)}/part-pickups`, body);
    if (!out.ok) {
      setPicked(prev => ({ ...prev, [k]: was }));
      setErrors(prev => ({ ...prev, [k]: out.message }));
    }
    setBusy(prev => ({ ...prev, [k]: false }));
  };

  return (
    <div className="mchat-parts" role="group" aria-label={`Parts to pick up, ${block.rangeLabel}`}>
      <div className="mchat-parts-head">
        <span className="mchat-parts-left" aria-live="polite">
          {total ? partsLeftLabel(left) : 'Nothing to tick'}
        </span>
        <span className="mchat-parts-range">{block.rangeLabel}</span>
      </div>

      {empty && <p className="mchat-parts-empty">{empty}</p>}

      {block.jobs.map(job => (
        <div className="mchat-parts-job" key={job.jobId}>
          <div className="mchat-group">{job.label}</div>
          {job.items.map(item => {
            const k = key(job.jobId, item.lineId);
            const p = picked[k] ?? null;
            const qty = partsQuantityLabel(item.quantity);
            const by = p ? (p.by === 'You' ? 'Picked up by you' : p.by ? `Picked up by ${p.by}` : 'Picked up') : null;
            return (
              <div className="mchat-parts-line" key={k}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={!!p}
                  aria-busy={busy[k] || undefined}
                  className={`mchat-parts-item${p ? ' mchat-parts-item--on' : ''}`}
                  disabled={!live || !!busy[k]}
                  onClick={() => { void toggle(job.jobId, item.lineId, item.source ?? 'quote'); }}
                >
                  <span className={`mchat-parts-box${p ? ' mchat-parts-box--on' : ''}`} aria-hidden="true">
                    {p && <Icon icon={Check} size={14} />}
                  </span>
                  <span className="mchat-parts-main">
                    <span className="mchat-parts-name">{item.name}</span>
                    {by && <span className="mchat-parts-by">{by}</span>}
                  </span>
                  {qty && <span className="mchat-parts-qty">{qty}</span>}
                </button>
                {errors[k] && <p className="mchat-body mchat-error" role="alert">{errors[k]}</p>}
              </div>
            );
          })}
        </div>
      ))}

      {footnote && <p className="mchat-parts-foot">{footnote}</p>}
      {total > 0 && (
        <p className="mchat-parts-saved">
          {live && <Icon icon={Check} size={13} className="mchat-parts-saved-icon" />}
          {live
            ? 'Saved as you tick, so the whole team sees it'
            : 'As it stood when you asked. Ask again for the latest.'}
        </p>
      )}

      <BubbleRows rows={[{ id: 'open-week', title: block.openLabel, onPress: () => onOpen(block.openPath) }]} />
    </div>
  );
}
